/**
 * <AutoCoderModal> — modal that runs Claude auto-coding against a note and
 * lets the user apply suggested claim lines back into a parent form.
 *
 * Backend: POST /api/billing-ai/auto-code (Claude, closed-world catalog,
 * RAG-augmented with org's billing namespace).
 *
 * Confidence ≥ 0.7 is auto-submit eligible (one-click apply); below requires
 * an explicit checkbox per row.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Sparkles, X, CheckCircle, AlertTriangle, ShieldCheck } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface CodingSuggestion {
  billing_activity_id: number;
  activity_code: string;
  cpt_or_hcpcs: string;
  modifiers: string[];
  dx_codes: string[];
  place_of_service: string | null;
  units: number;
  confidence: number;
  rationale: string;
  auto_submit_eligible: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  note: string;
  programId: number;
  durationMinutes?: number;
  /** Called when user accepts one or more suggestions */
  onApply: (suggestions: CodingSuggestion[]) => void;
}

const CLAUDE_MODELS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6 (default)' },
  { value: 'claude-opus-4-7', label: 'Opus 4.7 (highest quality)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5 (fastest)' },
];

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function AutoCoderModal({
  open,
  onClose,
  note,
  programId,
  durationMinutes,
  onApply,
}: Props) {
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<CodingSuggestion[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  async function runCode() {
    setBusy(true);
    setError(null);
    setSuggestions(null);
    setSelected(new Set());
    try {
      const r = await fetch(getWeaveinApiUrl('/api/billing-ai/auto-code'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          note,
          program_id: programId,
          duration_minutes: durationMinutes ?? null,
          model,
        }),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const data = await r.json();
      const list: CodingSuggestion[] = data.suggestions || [];
      setSuggestions(list);
      // Pre-select auto-submit-eligible rows
      setSelected(
        new Set(
          list
            .map((s, i) => (s.auto_submit_eligible ? i : -1))
            .filter((i) => i >= 0)
        )
      );
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  function apply() {
    if (!suggestions) return;
    const picks = suggestions.filter((_, i) => selected.has(i));
    if (picks.length === 0) {
      onClose();
      return;
    }
    onApply(picks);
    onClose();
  }

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mt-[8vh] w-full max-w-3xl overflow-hidden rounded-xl border border-v2border bg-paper shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-v2border bg-surface px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-v2accent" />
            <h2 className="text-sm font-semibold text-ink">Claude auto-coder</h2>
            <span className="text-xs text-ink-500">program #{programId}</span>
          </div>
          <button onClick={onClose} className="text-ink-500 hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <div className="mb-3 flex items-start gap-2 rounded-md border border-v2border-muted bg-paper-2 px-3 py-2 text-xs text-ink-500">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-v2accent" />
            Closed-world: suggestions are limited to your program's billing activities. Note is
            sent to Claude with org-private RAG context; never crosses tenants.
          </div>

          <div className="mb-3 flex items-end gap-3">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Model
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="rounded-md border border-v2border bg-paper px-2 py-1 text-sm text-ink"
              >
                {CLAUDE_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={runCode}
              disabled={busy || !note.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {suggestions === null ? 'Suggest coding' : 'Re-suggest'}
            </button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {suggestions && suggestions.length === 0 && (
            <p className="rounded-md border border-v2border-muted bg-paper-2 p-3 text-sm text-ink-500">
              No suggestions. The note may be too thin or doesn't match any of this program's
              billing activities.
            </p>
          )}

          {suggestions && suggestions.length > 0 && (
            <div className="overflow-hidden rounded-md border border-v2border-muted">
              <table className="w-full text-sm">
                <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="w-8 px-2 py-2"></th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Activity</th>
                    <th className="px-3 py-2 text-left">Mods</th>
                    <th className="px-3 py-2 text-left">Dx</th>
                    <th className="px-3 py-2 text-right">Units</th>
                    <th className="px-3 py-2 text-right">Conf.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-v2border-muted">
                  {suggestions.map((s, i) => {
                    const checked = selected.has(i);
                    return (
                      <tr
                        key={i}
                        className={`cursor-pointer hover:bg-paper-2/50 ${checked ? 'bg-emerald-50/30' : ''}`}
                        onClick={() => toggle(i)}
                      >
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(i)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-3.5 w-3.5 accent-v2accent"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <code className="rounded bg-paper-2 px-1.5 py-0.5 text-[12px] text-ink">
                            {s.cpt_or_hcpcs}
                          </code>
                        </td>
                        <td className="px-3 py-2 text-ink" title={s.rationale}>
                          {s.activity_code}
                        </td>
                        <td className="px-3 py-2 text-ink-500">{s.modifiers.join(', ') || '—'}</td>
                        <td className="px-3 py-2 text-ink-500">{s.dx_codes.join(', ') || '—'}</td>
                        <td className="px-3 py-2 text-right text-ink">{s.units}</td>
                        <td
                          className={`px-3 py-2 text-right font-medium ${
                            s.auto_submit_eligible ? 'text-emerald-600' : 'text-amber-600'
                          }`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {s.auto_submit_eligible ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <AlertTriangle className="h-3 w-3" />
                            )}
                            {(s.confidence * 100).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-v2border bg-surface px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-v2border bg-paper px-3 py-1.5 text-sm font-medium text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!suggestions || selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Apply {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
